import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import Button from '../common/Button';
import {
  CompanyInfoDTO,
  CompetitionEntryDTO,
  CourseDTO,
  InvoiceDTO,
  MemberDTO,
  PaymentDTO,
  RplDTO,
} from '../../services/apiClient';
import {
  PAYFAST_MERCHANT_ID,
  PAYFAST_MERCHANT_KEY,
  PAYFAST_URL,
  API_BASE_URL,
  BASE_URL,
  PAYFAST_PASSPHRASE,
} from '../../config/config';
import CryptoJS from 'crypto-js';
import { showAlert, confirmAction } from '../../utils/sweetAlertUtils';

import {
  CircleX,
  Clock,
  Fingerprint,
  Globe,
  Landmark,
  Mail,
  RotateCcw,
} from 'lucide-react';
import { usePaymentApi } from '../../hooks/usePaymentApi';
import { usePayFastApi } from '../../hooks/usePayFastApi';
import { useInvoiceApi } from '../../hooks/useInvoiceApi';
import { useCompanyInfoApi } from '../../hooks/useCompanyInfoApi';
import { CompanyConstants } from '../../constants/CompanyConstants';
import ProofUpload from '../common/ProofUpload';
import { ComponentConstants } from '../../constants/ComponentConstants';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InvoiceTypeConstants } from '../../constants/InvoiceTypeConstants';
import { InvoiceStatusConstants } from '../../constants/InvoiceStatusConstants';

export interface PaymentConfig {
  amount: number;
  itemName: string;
  description: string;
  reference: string;
  allowOnlinePayment?: boolean;
  allowEftPayment?: boolean;
  allowDebitOrder?: boolean;
  payFastTransactionFee?: number;
  totalWithPayFastFee?: number;
}

export interface PaymentStatus {
  isProcessing?: boolean;
  isPending?: boolean;
  isUploaded?: boolean;
  isFailed?: boolean;
  canRetry?: boolean;
}

export interface PaymentCallbacks {
  // Return false (or resolve to false) to abort the PayFast redirect, e.g. when the server says payment is no longer allowed
  onPaymentInitiated?: (paymentId: string) => void | boolean | Promise<void | boolean>;
  onPaymentSuccess?: () => void;
  onPaymentFailed?: () => void;
  onProofUploaded?: (fileRecordId: string) => void | Promise<void>;
  onProofPending?: () => void | Promise<void>;
  onRetryPayment?: () => void;
  onResetFromProcessing?: () => void | Promise<void>;
  onCancel?: () => void;
}

interface PaymentProps {
  member: MemberDTO;
  rpl?: RplDTO;
  course?: CourseDTO;
  competitionEntry?: CompetitionEntryDTO;
  componentId: string;
  paymentConfig: PaymentConfig;
  paymentStatus?: PaymentStatus;
  callbacks?: PaymentCallbacks;
  title?: string;
  showMemberDetails?: boolean;
  type: string;
  existingInvoiceId?: string;  // Pass invoice.id when paying an existing invoice
  existingInvoice?: InvoiceDTO;  // Optional: pass full invoice for instant debit order check
}

type PaymentMethod = 'Online Payment' | 'EFT Payment' | 'Debit Order';

// PayFast validates signatures with PHP's urlencode, which (unlike encodeURIComponent)
// also encodes ! ' ( ) * ~ and uses + for spaces — e.g. "O'Brien" must become "O%27Brien".
const phpUrlEncode = (value: string): string =>
  encodeURIComponent(value)
    .replace(/[!'()*~]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)
    .replace(/%20/g, '+');

const generateSignature = (
  params: { [key: string]: string },
  passphrase?: string,
): string => {
  const queryString = Object.keys(params)
    .map((key) => `${phpUrlEncode(key)}=${phpUrlEncode(params[key])}`)
    .join('&');

  const stringToHash = passphrase
    ? `${queryString}&passphrase=${phpUrlEncode(passphrase.trim())}`
    : queryString;

  const signature = CryptoJS.MD5(stringToHash).toString(CryptoJS.enc.Hex);
  return signature;
};

const Payment: React.FC<PaymentProps> = ({
  member,
  rpl,
  course,
  competitionEntry,
  componentId,
  paymentConfig,
  paymentStatus = {},
  callbacks = {},
  title = 'Make a payment',
  showMemberDetails = true,
  type,
  existingInvoiceId,
  existingInvoice,
}) => {
  const [paymentNotice, setPaymentNotice] = useState<PaymentDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    isProcessing = false,
    isPending = false,
    isUploaded = false,
    isFailed = false,
    canRetry = true,
  } = paymentStatus;

  const {
    allowOnlinePayment = true,
    allowEftPayment = true,
    allowDebitOrder: allowDebitOrderFromConfig,
  } = paymentConfig;

  // Debit orders available for renewal invoices or when explicitly enabled via config
  const allowDebitOrder = allowDebitOrderFromConfig ||
    existingInvoice?.invoiceTypeId === InvoiceTypeConstants.SALES_ORDER_RENEWAL;

      // Determine initial payment type from URL parameter
  const getInitialPaymentType = (): PaymentMethod => {
    const methodParam = searchParams.get('method');

    if (methodParam === 'debit' && allowDebitOrder) {
      return 'Debit Order';
    }
    if (methodParam === 'eft' && allowEftPayment) {
      return 'EFT Payment';
    }
    if (methodParam === 'online' && allowOnlinePayment) {
      return 'Online Payment';
    }
    // Default to EFT wherever it is allowed, then fall back to whatever else is enabled
    if (allowEftPayment) {
      return 'EFT Payment';
    }
    if (allowOnlinePayment) {
      return 'Online Payment';
    }
    if (allowDebitOrder) {
      return 'Debit Order';
    }
    return 'EFT Payment';
  };

  const [paymentType, setPaymentType] = useState<PaymentMethod>(getInitialPaymentType());

  const { search, create } = usePaymentApi();
  const { getById: getInvoiceById } = useInvoiceApi();
  const [existingPaymentForInvoice, setExistingPaymentForInvoice] = useState<PaymentDTO | null>(null);

  // If an invoice already exists for this payment (found via paymentNotice, or passed in
  // directly), the amount charged must match what that invoice was created with — never
  // re-derive it from the member/course/rpl's current fee configuration. That fee can change
  // after the invoice was created (e.g. an admin edits the course fee), and charging the new
  // amount against the old invoice is what causes TotalPaid to exceed the invoice's TotalIncl.
  const invoiceBaseAmount = paymentNotice?.invoice?.totalIncl ?? existingInvoice?.totalIncl;
  const baseAmount = invoiceBaseAmount ?? paymentConfig.amount;
  // Read via a ref inside payOnline (see below) so the invariant check there always compares
  // against the current base amount, not one captured by a stale memoized closure.
  const baseAmountRef = useRef<number>(baseAmount);
  useEffect(() => {
    baseAmountRef.current = baseAmount;
  }, [baseAmount]);

  const { calculateFee } = usePayFastApi();
  const [computedPayFastFee, setComputedPayFastFee] = useState<number | undefined>(paymentConfig.payFastTransactionFee);
  const [computedTotalWithFee, setComputedTotalWithFee] = useState<number | undefined>(paymentConfig.totalWithPayFastFee);
  // Starts true whenever we have an existing invoice amount to recalculate against, so the
  // "pay" button stays disabled until we know the real PayFast total - we must never let a
  // card payment go out for the base amount only (customer would be charged less than the
  // invoice total, and the fee would go uncollected).
  const [isFeeCalculating, setIsFeeCalculating] = useState<boolean>(!!invoiceBaseAmount && invoiceBaseAmount > 0);
  const [feeCalculationFailed, setFeeCalculationFailed] = useState(false);
  const [feeRetryCount, setFeeRetryCount] = useState(0);

  useEffect(() => {
    if (!invoiceBaseAmount || invoiceBaseAmount <= 0) {
      // No existing invoice yet - mirror whatever the parent has computed for the new invoice.
      // This must stay in sync with paymentConfig on every change (not just on mount), because
      // the parent (e.g. MemberSetup) can resolve its own async fee calculation after this
      // component has already mounted (e.g. on a page refresh mid payment-step) - otherwise the
      // pay button would stay stuck disabled even once the real fee becomes known.
      setComputedPayFastFee(paymentConfig.payFastTransactionFee);
      setComputedTotalWithFee(paymentConfig.totalWithPayFastFee);
      setIsFeeCalculating(false);
      setFeeCalculationFailed(false);
      return;
    }

    let cancelled = false;
    setIsFeeCalculating(true);
    setFeeCalculationFailed(false);

    calculateFee(invoiceBaseAmount)
      .then((response) => {
        if (cancelled) return;
        if (!response?.data) {
          setFeeCalculationFailed(true);
          return;
        }
        setComputedPayFastFee(response.data.payFastFee);
        setComputedTotalWithFee(response.data.totalWithFee);
      })
      .catch((error) => {
        console.error('Error recalculating PayFast fee for existing invoice:', error);
        if (!cancelled) setFeeCalculationFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsFeeCalculating(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceBaseAmount, feeRetryCount, paymentConfig.payFastTransactionFee, paymentConfig.totalWithPayFastFee]);

  // The PayFast fee must be known and reflected in the charge amount before we let the user
  // pay by card - never fall back to charging just the base amount.
  const isOnlinePaymentReady = !isFeeCalculating && !feeCalculationFailed && !!computedTotalWithFee && computedTotalWithFee > 0;

  const formattedAmount = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
  }).format(baseAmount);

  const formattedPayFastFee = computedPayFastFee
    ? new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
    }).format(computedPayFastFee)
    : null;

  const formattedTotalWithFee = computedTotalWithFee
    ? new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 2,
    }).format(computedTotalWithFee)
    : null;

  const fetchPayment = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters: {
          ...filters,
          isDeleted: false,
        },
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response?.data?.results && response.data.results.length > 0) {
        setPaymentNotice(response.data.results[0]);
      }

    } catch (error) {
      console.error('Error fetching Rpls:', error);
    }
  };

  const { getById: getCompanyInfoById } = useCompanyInfoApi();
  const [companyInfo, setCompanyInfo] = useState<CompanyInfoDTO | null>(null);
  const fetchCompanyInfo = async (): Promise<void> => {
    try {
      const response = await getCompanyInfoById(CompanyConstants.NATSHOOT);
      if (response) {
        setCompanyInfo(response.data || null);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    }
  };

  useEffect(() => {
    // If paying existing invoice, search for Payment by invoiceId
    if (existingInvoiceId) {
      fetchPayment(1, 1, { invoiceId: existingInvoiceId });
    } else {
      fetchPayment(1, 1, { memberId: member?.id, rplId: rpl?.id, courseId: course?.id, competitionEntryId: competitionEntry?.id });
    }
    fetchCompanyInfo();
  }, [existingInvoiceId]);

  const createPaymentNotice = async (): Promise<string | undefined> => {
    try {
      const payment = {
        memberId: member?.id,
        rplId: rpl?.id,
        courseId: course?.id,
        competitionEntryId: competitionEntry?.id,
        invoiceId: existingInvoiceId
      } as PaymentDTO;
      const response = await create(payment);
      return response?.data?.id;
    } catch (error) {
      console.error('Error creating payment', error);
      throw error;
    }
  };

  const redirectToPayFast = ({
    amount,
    recordId,
    userFirstName,
    userLastName,
    userEmail,
    itemName,
    returnUrl,
    cancelUrl,
    notifyUrl,
  }: {
    amount: number;
    recordId: string;
    userFirstName?: string;
    userLastName?: string;
    userEmail?: string;
    itemName: string;
    returnUrl: string;
    cancelUrl: string;
    notifyUrl: string;
  }) => {
    const paymentParams: { [key: string]: string } = {
      merchant_id: PAYFAST_MERCHANT_ID,
      merchant_key: PAYFAST_MERCHANT_KEY,
      return_url: returnUrl,
      cancel_url: cancelUrl,
      notify_url: notifyUrl,
      name_first: (userFirstName || '').trim(),
      name_last: (userLastName || '').trim(),
      email_address: (userEmail || '').trim().toLowerCase(),
      m_payment_id: recordId,
      amount: amount.toFixed(2),
      item_name: itemName.trim(),
    };

    const signature = generateSignature(paymentParams, PAYFAST_PASSPHRASE);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = PAYFAST_URL;

    Object.entries(paymentParams).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    const signatureInput = document.createElement('input');
    signatureInput.type = 'hidden';
    signatureInput.name = 'signature';
    signatureInput.value = signature;
    form.appendChild(signatureInput);

    document.body.appendChild(form);
    form.submit();
  };

  const payOnline = async (paymentNoticeId: string) => {
    // Fail closed: if the parent can't record that a payment is starting (e.g. registration already completed
    // in another tab), never send the member to PayFast to pay a second time.
    const canProceed = await callbacks.onPaymentInitiated?.(paymentNoticeId);
    if (canProceed === false) {
      throw new Error('Your payment has already been received. No further payment is required.');
    }

    // If on payment confirmation page (from email), redirect to login after payment
    // Otherwise, return to the current page
    const isConfirmationPage = window.location.pathname.includes('/payment/confirmation');
    const back = isConfirmationPage
      ? encodeURIComponent('/login')
      : encodeURIComponent(window.location.pathname + window.location.search);

    // Final authoritative recalculation, done synchronously right before we build the PayFast
    // form. Everything above (the displayed fee, the ready-gate) can only ever be as fresh as
    // the last time an effect happened to run - there's no guarantee the user clicked "Pay" in
    // the same instant. Recalculating here against the current base amount closes that gap: the
    // amount we send is always computed from what the invoice is worth right now, not from
    // whatever was cached whenever the page/effect last touched it. If this call fails for any
    // reason we abort rather than silently falling back to a cached or base amount.
    const currentBaseAmount = baseAmountRef.current;
    let amountToCharge: number;
    try {
      const freshFeeResponse = await calculateFee(currentBaseAmount);
      const freshTotal = freshFeeResponse?.data?.totalWithFee;
      if (!freshTotal || freshTotal <= currentBaseAmount) {
        throw new Error('Fee calculation did not return a valid fee-inclusive total.');
      }
      amountToCharge = freshTotal;
    } catch (error) {
      console.error('Error recalculating PayFast fee immediately before redirect:', error);
      throw new Error('Could not confirm the PayFast fee. Please try again.');
    }

    redirectToPayFast({
      amount: amountToCharge,
      recordId: paymentNoticeId,
      userFirstName: member?.firstName,
      userLastName: member?.lastName,
      userEmail: member?.email,
      itemName: paymentConfig.itemName,
      returnUrl: `${BASE_URL}payment/return?status=success&back=${back}`,
      cancelUrl: `${BASE_URL}payment/return?status=cancel&back=${back}&member=${member?.id}`,
      notifyUrl: `${API_BASE_URL}/payments/payfast/subscription/type/${type}`,
    });
  };

  const handlePaymentOption = useCallback(
    async (method: PaymentMethod) => {
      // Early return if already loading
      if (loading) return;

      // Fail closed: this component can be reached via a stale "pay now" link that stays valid
      // indefinitely, so refuse to submit any payment for an invoice already marked paid, even
      // if some upstream page-load guard was skipped or bypassed. `existingInvoice` is only a
      // snapshot taken whenever this page/tab first loaded - if the same invoice was paid in
      // another tab since then, that snapshot is stale, so re-check against the server right
      // before submitting rather than trusting it.
      const cachedInvoiceAlreadyPaid =
        existingInvoice?.invoiceStatusId?.toLowerCase() ===
          InvoiceStatusConstants.INVOICED || !!existingInvoice?.paidDate;
      if (cachedInvoiceAlreadyPaid) {
        setError('This invoice has already been paid. No further payment is required.');
        return;
      }

      if (existingInvoiceId) {
        try {
          const liveInvoiceResponse = await getInvoiceById(existingInvoiceId);
          const liveInvoice = liveInvoiceResponse?.data;
          const liveInvoiceAlreadyPaid =
            liveInvoice?.invoiceStatusId?.toLowerCase() ===
              InvoiceStatusConstants.INVOICED || !!liveInvoice?.paidDate;
          if (liveInvoiceAlreadyPaid) {
            setError('This invoice has already been paid. No further payment is required.');
            return;
          }
        } catch (error) {
          console.error('Error re-checking invoice status before payment:', error);
          setError('Could not confirm this invoice is still payable. Please refresh and try again.');
          return;
        }
      }

      if (method === 'Online Payment' && !isOnlinePaymentReady) {
        setError('Still calculating the PayFast fee - please wait a moment before paying.');
        return;
      }

      setError(null);
      setLoading(true);

      try {
        // Get or create payment notice ID
        const paymentNoticeId = await getOrCreatePaymentNoticeId();

        if (!paymentNoticeId) {
          throw new Error('Failed to get or create payment notice');
        }

        // Process payment based on selected method
        await processPaymentByMethod(method, paymentNoticeId);

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        setError(errorMessage);
        callbacks.onPaymentFailed?.();
        console.error('Payment error:', error);
      } finally {
        setLoading(false);
      }
    },
    [paymentConfig.amount, member, callbacks, loading, paymentNotice, isOnlinePaymentReady],
  );

  const getOrCreatePaymentNoticeId = async (): Promise<string | null> => {
    // If paying existing invoice, use the Payment we found (or error if none exists)
    if (existingInvoiceId) {
      if (paymentNotice?.id) {
        // Payment exists for this invoice - use it
        return paymentNotice.id;
      } else {
        // No Payment exists for this invoice - create one linked to existing invoice
        // The backend will use the existing invoice instead of creating a new one
        const newPaymentNoticeId = await createPaymentNotice();
        return newPaymentNoticeId || null;
      }
    }

    // Use existing payment notice if available (for normal flow)
    if (paymentNotice?.id) {
      return paymentNotice.id;
    }

    // Create new payment notice (this creates new Payment + new Invoice)
    const newPaymentNoticeId = await createPaymentNotice();
    return newPaymentNoticeId || null;
  };

  const processPaymentByMethod = async (method: PaymentMethod, paymentNoticeId: string): Promise<void> => {
    switch (method) {
      case 'Online Payment':
        await payOnline(paymentNoticeId);
        break;
      case 'EFT Payment':
        // EFT payment logic would go here if needed
        throw new Error('EFT payment processing not implemented in this method');
      case 'Debit Order':
        // Debit order logic would go here if needed
        throw new Error('Debit order processing not implemented in this method');
      default:
        throw new Error(`Unsupported payment method: ${method}`);
    }
  };

  const handleCancel = () => {
    callbacks.onCancel?.();
  };

  const handleProofUploaded = (fileRecordId: string) => {
    callbacks.onProofUploaded?.(fileRecordId);
  };

  const handleUploadLater = async () => {
    await callbacks.onProofPending?.();
    await showAlert({
      title: 'Email Sent',
      text: 'An email has been sent with a link to upload your proof of payment.',
      icon: 'success',
      confirmButtonText: 'OK',
    });
  };

  const handleRetry = () => {
    callbacks.onRetryPayment?.();
  };

  const canMakePayment = !isProcessing && !isPending && !isUploaded;
  const showRetryButton = isFailed && canRetry;

  return (
    <div className="flex flex-col space-y-4">
      {/* Payment Summary Section */}
      <div className="w-full">

        <div className="p-4 rounded mb-4 m-0" style={{ padding: '0' }}>
          <div className="flex justify-between">
            <span className="text-sm sm:text-base">{paymentConfig.itemName}</span>
            <span className="font-bold text-sm sm:text-base">{formattedAmount}</span>
          </div>
          <div>
            <span className="text-xs sm:text-sm text-gray-600">{paymentConfig.description}</span>
          </div>
        </div>

        {canMakePayment ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            {allowOnlinePayment && (
              <Button
                className={`w-full text-sm ${paymentType === 'Online Payment' ? 'bg-secondary' : ''}`}
                disabled={loading}
                onClick={() => setPaymentType('Online Payment')}
                icon={Globe}
              >
                Online
              </Button>
            )}

            {allowEftPayment && (
              <Button
                className={`w-full text-sm ${paymentType === 'EFT Payment' ? 'bg-secondary' : ''}`}
                disabled={loading}
                onClick={() => setPaymentType('EFT Payment')}
                icon={Landmark}
              >
                EFT
              </Button>
            )}

            {allowDebitOrder && (
              <Button
                className={`w-full text-sm ${paymentType === 'Debit Order' ? 'bg-secondary' : ''}`}
                disabled={loading}
                onClick={() => setPaymentType('Debit Order')}
                icon={Clock}
              >
                Debit
              </Button>
            )}

          </div>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            <Button
              className="w-full text-sm"
              disabled={loading}
              onClick={() => navigate('/contact')}
              icon={Mail}
            >
              Contact Admin
            </Button>

            {showRetryButton && (
              <Button
                className="w-full text-sm"
                disabled={loading}
                onClick={handleRetry}
                icon={RotateCcw}
              >
                Try Again
              </Button>
            )}

            {isProcessing && (
              <Button
                className="w-full text-sm"
                disabled={loading}
                onClick={async () => {
                  const confirmed = await confirmAction({
                    title: 'Reset Payment Status?',
                    text: 'This will reset your payment status and allow you to retry. Continue?',
                    icon: 'warning',
                    confirmButtonText: 'Yes, Reset',
                    cancelButtonText: 'Cancel',
                  });
                  if (confirmed) {
                    await callbacks.onResetFromProcessing?.();
                  }
                }}
                icon={RotateCcw}
              >
                Reset Payment
              </Button>
            )}
          </div>
        )}

        {/* Payment Details Section */}
        <div className="w-full">

          {/* <h2>{`${paymentType} Selected`}</h2> */}

          {paymentType === 'Online Payment' && (
            <>
              {canMakePayment ? (
                <>
                  {/* PayFast Fee Breakdown */}
                  {isFeeCalculating && (
                    <div className="p-3 sm:p-4 rounded mb-4 bg-gray-50 dark:bg-gray-700">
                      <p className="text-xs sm:text-sm">Calculating PayFast fee...</p>
                    </div>
                  )}

                  {feeCalculationFailed && (
                    <div className="p-3 sm:p-4 rounded mb-4 bg-red-50 dark:bg-red-900/20">
                      <p className="text-xs sm:text-sm">
                        Could not calculate the PayFast fee. Please try again.
                      </p>
                    </div>
                  )}

                  {formattedPayFastFee && formattedTotalWithFee && (
                    <div className="p-3 sm:p-4 rounded mb-4 bg-blue-50 dark:bg-blue-900/20">
                      <h3 className="font-bold mb-2 text-sm sm:text-base">Payment Breakdown</h3>
                      <div className="space-y-1 text-xs sm:text-sm">
                        <div className="flex justify-between">
                          <span>Base Amount:</span>
                          <span>{formattedAmount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>*PayFast Processing Fee:</span>
                          <span>{formattedPayFastFee}</span>
                        </div>
                        <div className="border-t border-blue-200 dark:border-blue-700 my-2"></div>
                        <div className="flex justify-between font-bold">
                          <span>Total to Pay:</span>
                          <span>{formattedTotalWithFee}</span>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-blue-800 dark:text-blue-200">
                        <p className="font-semibold mb-1">*Please note:</p>
                        <p>
                          Payments made via PayFast are subject to this processing fee,
                          which has been added to your total.
                          Debit order and EFT payments do not incur any additional fees.
                        </p>
                      </div>
                    </div>
                  )}

                  {showMemberDetails && (
                    <div className="p-3 sm:p-4 rounded mb-4 bg-gray-50 dark:bg-gray-700">
                      <h3 className="font-bold mb-2 text-sm sm:text-base">Personal Details</h3>
                      <div className="space-y-1 text-xs sm:text-sm">
                        <p className="break-words">Name: {member?.firstName} {member?.lastName}</p>
                        <p className="break-all">Email: {member?.email}</p>
                      </div>
                    </div>
                  )}

                  <div className="p-3 sm:p-4 rounded mb-4 bg-blue-50 dark:bg-blue-900/20">
                    <h3 className="font-bold mb-2 text-sm sm:text-base">
                      You will be redirected to PAYFAST
                    </h3>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
                    {feeCalculationFailed ? (
                      <Button
                        className="w-full sm:w-auto text-sm"
                        disabled={loading}
                        onClick={() => setFeeRetryCount((c) => c + 1)}
                        icon={RotateCcw}
                      >
                        Retry Fee Calculation
                      </Button>
                    ) : (
                      <Button
                        className="w-full sm:w-auto text-sm"
                        disabled={loading || !isOnlinePaymentReady}
                        isLoading={loading}
                        onClick={() => handlePaymentOption('Online Payment')}
                        icon={Fingerprint}
                      >
                        Make a Secure Payment
                      </Button>
                    )}


                    <Button
                      className="w-full sm:w-auto text-sm"
                      disabled={loading}
                      onClick={() => handleCancel()}
                      icon={CircleX}
                    >
                      Cancel
                    </Button>

                  </div>


                </>
              ) : (
                <>
                  {isProcessing && (
                    <div className="p-3 sm:p-4 rounded mb-4 bg-yellow-50 dark:bg-yellow-900/20">
                      <h3 className="font-bold mb-2 text-sm sm:text-base">
                        Your payment is processing. It usually completes within
                        5 minutes. If the status has not updated after that,
                        you can reset the payment status to try again.
                      </h3>
                    </div>
                  )}

                  {isFailed && (
                    <div className="p-3 sm:p-4 rounded mb-4 bg-red-50 dark:bg-red-900/20">
                      <h3 className="font-bold mb-2 text-sm sm:text-base">
                        Your payment could not be completed. Please try again or
                        contact the admin for assistance.
                      </h3>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {paymentType === 'EFT Payment' && (
            <>
              {!isPending && !isUploaded ? (
                <>
                  <div className="p-3 sm:p-4 rounded mb-4 bg-gray-50 dark:bg-gray-700">
                    <h3 className="font-bold mb-2 text-sm sm:text-base">Banking Details</h3>
                    <div className="space-y-1 text-xs sm:text-sm">
                      <p>Bank: {companyInfo?.bank}</p>
                      <p className="break-words">Account Name: {companyInfo?.name}</p>
                      <p>Account Type: {companyInfo?.accountType}</p>
                      <p className="break-all">Account Number: {companyInfo?.accountNumber}</p>
                      <p>Branch Code: {companyInfo?.branchCode}</p>
                      <p>Amount: <span className="font-bold">{formattedAmount}</span></p>
                      <p className="mt-2 break-all">
                        Reference: <span className="font-bold">{paymentConfig.reference}</span>
                      </p>
                    </div>
                  </div>

                  <div className="p-3 sm:p-4 rounded">
                    <h3 className="font-bold text-sm sm:text-base">Upload Proof of Payment</h3>
                  </div>
                </>
              ) : (
                <>
                  {isPending && (
                    <div className="p-3 sm:p-4 rounded mb-4 bg-yellow-50 dark:bg-yellow-900/20">
                      <h3 className="font-bold mb-2 text-sm sm:text-base">
                        Your proof of payment is pending. Upload it now to
                        activate your access and start enjoying all our services.
                      </h3>
                    </div>
                  )}
                  {isUploaded && (
                    <div className="p-3 sm:p-4 rounded mb-4 bg-green-50 dark:bg-green-900/20">
                      <h3 className="font-bold mb-2 text-sm sm:text-base">
                        Your proof of payment has been uploaded. Verification
                        may take up to 3 business days, after which your access
                        will be activated.
                      </h3>
                    </div>
                  )}
                </>
              )}

              {/* Hidden while a card payment is processing - it may complete any moment, and uploading on top of it caused duplicate/reverted registrations */}
              {!isUploaded && !isPending && !isProcessing && (
                <ProofUpload
                  title={title}
                  memberId={member.id}
                  entityId={member.id || rpl?.id || course?.id || competitionEntry?.id}
                  componentId={componentId}
                  onUploadSuccess={handleProofUploaded}
                  onUploadLater={handleUploadLater}
                  isPending={isPending}
                  isUploaded={isUploaded}
                />
              )}

            </>
          )}

          {paymentType === 'Debit Order' && (
            <>
              <div className="p-3 sm:p-4 rounded mt-4 mb-4 bg-blue-50 dark:bg-blue-900/20">
                <h3 className="font-bold mb-2 text-sm sm:text-base">
                  You will be redirected to a Debit Order Mandate Form
                </h3>
              </div>

              <Button
                className="w-full sm:w-auto text-sm"
                disabled={loading}
                onClick={() => {
                  const invoiceId = existingInvoiceId || paymentNotice?.invoice?.id;
                  if (invoiceId) {
                    navigate(`/debit-order-mandate?invoiceId=${invoiceId}`);
                  } else {
                    showAlert({
                      title: 'Error',
                      text: 'Invoice information not available. Please try again.',
                      icon: 'error',
                      confirmButtonText: 'OK',
                    });
                  }
                }}
                icon={Fingerprint}
              >
                Continue
              </Button>
            </>
          )}


          {error && <p className="text-red-600 text-sm mt-2 break-words">{error}</p>}

        </div>
      </div>
    </div>
  );
};

export default Payment;
